import { Burden, VoidCallback, VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useEffect, useRef } from 'react';
import fastIsEqual from 'react-fast-compare';
import onClickOutside from 'react-onclickoutside';
import { useEvent } from 'react-use';
import { listen } from '../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../Legacy';

export interface PromptProps {
    name?: string;
    delay?: number;
    onAction: VoidCallback1<boolean>;
    enterRequiresCtrl?: boolean;
    ignoreMouse?: boolean;
    ignoreEnter?: boolean;
    ignoreEscape?: boolean;
    clickOffIsPositive?: boolean;
    disabled?: boolean;
    preventDefault?: boolean;
}

export const ClickOff = onClickOutside(
    class extends React.Component<{ onClick: VoidCallback, children?: React.ReactNode }> {

        public render() {
            return this.props.children;
        }

        public handleClickOutside = () => {
            this.props.onClick();
        };
    }
);

export const Prompt: LFC<PromptProps> = props => {

    const ignoreClass = !!props.name
        ? `ignore-${props.name}`
        : 'ignore-prompt';

    const onClick = useCallback((e: any) => {
        if (!props.disabled && !props.ignoreMouse) {
            if (props.preventDefault) e.preventDefault();
            props.onAction(!!props.clickOffIsPositive);
        }
    }, [props]);

    const onKey = useCallback((e: KeyboardEvent) => {
        if (e.ctrlKey && !props.enterRequiresCtrl) return;
        if (e.altKey) return;
        if (e.shiftKey) return;
        if (!props.disabled && !props.ignoreEnter && (e.key === 'Enter') && (!props.enterRequiresCtrl || e.ctrlKey)) {
            if (props.preventDefault) e.preventDefault();
            props.onAction(true);
        }
        else if (!props.disabled && !props.ignoreEscape && e.code === 'Escape') {
            if (props.preventDefault) e.preventDefault();
            props.onAction(false);
        }
    }, [props]);

    useEvent('keydown', onKey, document);

    return <ClickOff outsideClickIgnoreClass={ignoreClass} onClick={onClick}>
        {props.children}
    </ClickOff>;
};

const isOrIsDescendantOf = (subject: HTMLElement, targetOrSelector: HTMLElement | string) => {
    const tos = targetOrSelector;
    while (subject) {
        if (typeof (tos) === 'string' && subject.matches(tos)) return true;
        if (typeof (tos) === 'object' && subject === tos) return true;
        subject = subject.parentElement;
    }
    return false;
};

const PromptContext = React.createContext<string[]>([]);

export const PromptManager: LFC = ({ children }) =>
    <PromptContext.Provider value={[]}>
        {children}
    </PromptContext.Provider>;

export const usePrompt = (opts: PromptProps) => {
    const prevOpts = useRef<PromptProps>();
    const ref = useRef<any>();

    opts = fastIsEqual(prevOpts.current, opts) ? prevOpts.current : opts;
    prevOpts.current = opts;

    const onClick = useCallback((e: any) => {
        if (!opts.disabled && !opts.ignoreMouse) {
            if (opts.preventDefault) e.preventDefault();
            opts.onAction(!!opts.clickOffIsPositive);
        }
    }, [opts]);

    const onKey = useCallback((e: KeyboardEvent) => {
        if (e.ctrlKey && !opts.enterRequiresCtrl) return;
        if (e.altKey) return;
        if (e.shiftKey) return;
        if (!opts.disabled && !opts.ignoreEnter && (e.key === 'Enter') && (!opts.enterRequiresCtrl || e.ctrlKey)) {
            if (opts.preventDefault) e.preventDefault();
            opts.onAction(true);
        }
        else if (!opts.disabled && !opts.ignoreEscape && e.code === 'Escape') {
            if (opts.preventDefault) e.preventDefault();
            opts.onAction(false);
        }
    }, [opts]);

    useEffect(() => {
        if (!ref.current) return;

        const l1 = listen(window, 'keydown', onKey);
        const l2 = listen(window, 'mousedown', e => {
            if (isOrIsDescendantOf(e.target as any, ref.current)) return;
            if (isOrIsDescendantOf(e.target as any, `.ignore-${opts.name}`)) return;
            if (isOrIsDescendantOf(e.target as any, '[data-mantine-shared-portal-node]')) return;
            onClick(e);
        });

        return () => Burden.resolveAll([l1, l2]);
    }, [ref.current, opts]);

    return ref;
};
